import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext, useLocation } from 'react-router-dom';

interface UseUnsavedChangesPromptOptions {
  when: boolean;
  message?: string;
}

const DEFAULT_MESSAGE =
  'You have unsaved changes that will be lost if you leave this page. Are you sure you want to leave?';

const resolveToPathname = (to: unknown): string => {
  if (typeof to === 'string') return to.split(/[?#]/)[0];
  if (to && typeof to === 'object') {
    const pathname = (to as { pathname?: string }).pathname;
    return typeof pathname === 'string' ? pathname : '';
  }
  return '';
};

const parentOf = (pathname: string): string => pathname.split('/').slice(0, -1).join('/');

// PLA-8318: prompts the user before they leave a page with unsaved edits.
// Covers tab close / refresh / external nav via `beforeunload` and in-app
// SPA navigation by monkey-patching the router's `push`/`replace`. Relative
// paths (used for in-editor step navigation, e.g. `navigate('info', { relative: 'path' })`)
// are allowed through unprompted.
const useUnsavedChangesPrompt = ({
  when,
  message = DEFAULT_MESSAGE
}: UseUnsavedChangesPromptOptions): void => {
  useEffect(() => {
    if (!when) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  const { navigator, basename } = useContext(NavigationContext) as {
    navigator: {
      push: (...args: unknown[]) => unknown;
      replace: (...args: unknown[]) => unknown;
    };
    basename: string;
  };

  const location = useLocation();
  const currentPathnameRef = useRef(location.pathname);
  useEffect(() => {
    currentPathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!when) return undefined;
    const originalPush = navigator.push.bind(navigator);
    const originalReplace = navigator.replace.bind(navigator);

    // Scope = the editor's root path (parent of the current step path),
    // joined with basename because react-router prepends basename to
    // pathnames before calling navigator.push/replace.
    // E.g. activating at /p/rewards/create/info under basename /dashboard
    //   → scope = /dashboard/p/rewards/create.
    // Any navigation that stays inside this subtree (next/prev steps)
    // is allowed without prompting.
    const basePrefix = basename && basename !== '/' ? basename : '';
    const scopeAnchor = parentOf(currentPathnameRef.current);
    const scopeAnchorAbsolute = scopeAnchor ? `${basePrefix}${scopeAnchor}` : '';

    const isWithinScope = (next: string): boolean => {
      if (next === '') return true;
      if (!next.startsWith('/')) return true;
      if (!scopeAnchorAbsolute) return false;
      return next === scopeAnchorAbsolute || next.startsWith(`${scopeAnchorAbsolute}/`);
    };

    const guard =
      (original: (...args: unknown[]) => unknown) =>
      (...args: unknown[]) => {
        const nextPathname = resolveToPathname(args[0]);
        if (isWithinScope(nextPathname)) return original(...args);
        if (window.confirm(message)) return original(...args);
        return undefined;
      };

    navigator.push = guard(originalPush);
    navigator.replace = guard(originalReplace);

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator, basename, when, message]);
};

export { useUnsavedChangesPrompt };
export default useUnsavedChangesPrompt;
